import { useState } from 'react';
import { Icon } from '@/components/Icon';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';
import { showToast } from '@/lib/toast';
import { deleteAd, setAdActive, type AdRow } from '@/api/ads';
import { TileMenu } from '@/components/TileMenu';

/**
 * Плитка объявления — раскрывающаяся, как у шаблонов соглашения
 * (OperatorTemplateTile): объявлений немного, таблица со скроллом ради
 * трёх колонок избыточна. Раскрытие держит вкладка — раскрыт ровно один.
 */
export function AdTile({
  ad,
  expanded,
  onToggleExpand,
  onEditRequest,
  onDeleted,
  onPatched,
}: {
  ad: AdRow;
  expanded: boolean;
  onToggleExpand: () => void;
  onEditRequest: () => void;
  onDeleted: () => void;
  onPatched: (updated: AdRow) => void;
}) {
  const [busy, setBusy] = useState(false);
  const { confirm, dialog } = useConfirmDialog();

  const handleDelete = async () => {
    const ok = await confirm(
      `Удалить объявление «${ad.name}»? После удаления все данные будут удалены безвозвратно.`,
      { confirmLabel: 'Удалить', danger: true },
    );
    if (!ok) return;
    setBusy(true);
    try {
      await deleteAd(ad.id);
      showToast('Объявление удалено');
      onDeleted();
    } catch {
      showToast('Не удалось удалить объявление.');
      setBusy(false);
    }
  };

  const toggleActive = async () => {
    setBusy(true);
    try {
      const updated = await setAdActive(ad.id, !ad.active);
      onPatched(updated);
      showToast(updated.active ? 'Объявление включено' : 'Объявление отключено');
    } catch {
      showToast('Не удалось изменить показ объявления.');
    } finally {
      setBusy(false);
    }
  };

  const period = [ad.dateFrom, ad.dateTo].every(Boolean)
    ? `${formatDate(ad.dateFrom)} — ${formatDate(ad.dateTo)}`
    : 'Период не задан';

  return (
    <div className={`catalog-tile${expanded ? ' catalog-tile--expanded' : ''}`}>
      <div className="catalog-tile__main">
        <div className="catalog-tile__icon">
          <Icon name="megaphone" size={18} />
        </div>
        <button type="button" className="catalog-tile__body catalog-tile__body--clickable" onClick={onToggleExpand}>
          <div className="catalog-tile__name">
            {ad.name}
            <span className={`badge ${ad.active ? 'badge--good' : 'badge--neutral'}`}>
              {ad.active ? 'Включено' : 'Отключено'}
            </span>
          </div>
          <div className="catalog-tile__category">{period}</div>
        </button>

        <TileMenu
          disabled={busy}
          active={ad.active}
          onEdit={onEditRequest}
          onDelete={handleDelete}
          onToggleActive={toggleActive}
        />

        <button
          type="button"
          className="catalog-tile__expand"
          onClick={onToggleExpand}
          aria-expanded={expanded}
          aria-label={expanded ? `Скрыть «${ad.name}»` : `Показать «${ad.name}»`}
        >
          <Icon name="chevronDown" size={16} />
        </button>
      </div>

      {/* Медиафайл — маленькой превьюшкой слева от текста, как объявление
          выглядит в самом кабинете клиента, а не полосой во всю ширину. */}
      {expanded && (
        <div className="catalog-tile__reports ad-preview">
          {ad.media && (
            <div className="ad-preview__thumb">
              {ad.media.mime?.startsWith('video/') ? (
                <video src={ad.media.url ?? undefined} controls />
              ) : (
                <img src={ad.media.url ?? undefined} alt={ad.media.name ?? 'Медиафайл объявления'} />
              )}
            </div>
          )}
          {ad.description ? (
            // HTML набран тем же кругом админов (platform.ads) — доверяем так же, как раньше выводу Quill в Orchid.
            <div
              className="rich-text-preview ad-preview__text"
              dangerouslySetInnerHTML={{ __html: ad.description }}
            />
          ) : (
            <p className="cell-muted ad-preview__text">Текст объявления не задан.</p>
          )}
        </div>
      )}

      {dialog}
    </div>
  );
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  const [year, month, day] = value.split('-');
  return `${day}.${month}.${year}`;
}
